// Document numbering and edit-lease contracts: BranchSequencer formats and views, DocumentLock leases
// (modules-and-api.md §2.7, §3.1, §3.3, §5.3; TEN-20, TEN-25, TEN-84, R-11, R-23, rule 8, BR-23, OPN-09; decisions D22, D27).
import { z } from "zod";
import type { PermissionCode } from "./permissions";
import { GENERATED_BARCODE_PREFIX, InstantSchema, UlidSchema, gs1CheckDigit } from "./primitives";

// --- Sequences (UC-TENANCY-012..016) -------------------------------------------------------------------------------------

/** The sixteen per-branch document types (BR-TENANCY-018). Serial numbers are per tenant (TenantSequencer), not here. */
export const DOCUMENT_SEQUENCE_TYPES = [
  "Quote", "SalesOrder", "Invoice", "CreditNote", "Receipt", "Statement",
  "PurchaseOrder", "GoodsReceipt", "SupplierBill", "SupplierReturn",
  "LabOrder", "Prescription", "Referral",
  "StockAdjustment", "StockTake", "StockTransfer",
] as const;
export type DocumentSequenceType = (typeof DOCUMENT_SEQUENCE_TYPES)[number];
export const DocumentSequenceTypeSchema = z.enum(DOCUMENT_SEQUENCE_TYPES);
export const isDocumentSequenceType = (s: string): s is DocumentSequenceType => (DOCUMENT_SEQUENCE_TYPES as readonly string[]).includes(s);

/** Default prefix per document type (BR-TENANCY-019): `INV-MEN-000001`. */
export const DEFAULT_SEQUENCE_PREFIXES: Readonly<Record<DocumentSequenceType, string>> = {
  Quote: "Q", SalesOrder: "SO", Invoice: "INV", CreditNote: "CN", Receipt: "RCT", Statement: "STM",
  PurchaseOrder: "PO", GoodsReceipt: "GRN", SupplierBill: "BILL", SupplierReturn: "SRN",
  LabOrder: "LAB", Prescription: "RX", Referral: "REF",
  StockAdjustment: "ADJ", StockTake: "STK", StockTransfer: "TRF",
};

export const SEQUENCE_SEPARATORS = ["-", "/", ".", ""] as const;
export const SEQUENCE_PAD_WIDTH_MIN = 1;
export const SEQUENCE_PAD_WIDTH_MAX = 12;
export const SEQUENCE_PAD_WIDTH_DEFAULT = 6;

/** BR-TENANCY-019: prefix 1–6 uppercase letters or digits; separator `-`, `/`, `.` or none; pad width 1–12. */
export const SequenceFormatSchema = z.strictObject({
  prefix: z.string().regex(/^[A-Z0-9]{1,6}$/, "1 to 6 uppercase letters or digits"),
  separator: z.enum(SEQUENCE_SEPARATORS),
  includeBranchCode: z.boolean(),
  padWidth: z.number().int().min(SEQUENCE_PAD_WIDTH_MIN).max(SEQUENCE_PAD_WIDTH_MAX),
});
export type SequenceFormat = z.infer<typeof SequenceFormatSchema>;

/** PUT body: any subset of the format; unchanged fields keep their stored value (UC-TENANCY-013). */
export const SequenceFormatPatchSchema = z.strictObject({
  prefix: SequenceFormatSchema.shape.prefix.optional(),
  separator: SequenceFormatSchema.shape.separator.optional(),
  includeBranchCode: SequenceFormatSchema.shape.includeBranchCode.optional(),
  padWidth: SequenceFormatSchema.shape.padWidth.optional(),
}).refine((v) => Object.values(v).some((x) => x !== undefined), "at least one format field");
export type SequenceFormatPatch = z.infer<typeof SequenceFormatPatchSchema>;

export function defaultSequenceFormat(docType: DocumentSequenceType): SequenceFormat {
  return { prefix: DEFAULT_SEQUENCE_PREFIXES[docType], separator: "-", includeBranchCode: true, padWidth: SEQUENCE_PAD_WIDTH_DEFAULT };
}

/** Renders a counter in a format: `INV-MEN-000001`; a counter wider than the pad is never truncated (BR-TENANCY-019). */
export function formatDocumentNumber(format: SequenceFormat, branchCode: string, sequence: number): string {
  const parts = [format.prefix];
  if (format.includeBranchCode) parts.push(branchCode);
  parts.push(String(sequence).padStart(format.padWidth, "0"));
  return parts.join(format.separator);
}

/** BR-TENANCY-035: the practice's generated barcodes are numbered 1 to 9 999 999 999 — ten digits after the prefix. */
export const GENERATED_BARCODE_MAX = 9_999_999_999;

/**
 * BR-TENANCY-035, BR-CATALOGUE-015 (UC-TENANCY-019 step 3): a number of the tenant sequencer's barcode counter becomes the
 * practice's EAN-13 — `20`, the number as ten digits, then the GS1 check digit: 1 is `2000000000015`, 9 999 999 999 is
 * `2099999999998`. Past ten digits the counter refuses rather than lengthen the code: anything that is not a whole number
 * from 1 to 9 999 999 999 is a `RangeError` (a `TypeError` when it is not a number at all).
 */
export function formatGeneratedBarcode(n: number): string {
  if (typeof n !== "number") throw new TypeError(`a barcode number is a number, got ${typeof n}`);
  if (!Number.isInteger(n) || n < 1 || n > GENERATED_BARCODE_MAX) throw new RangeError(`a barcode number is a whole number from 1 to ${GENERATED_BARCODE_MAX}, got ${n}`);
  const body = GENERATED_BARCODE_PREFIX + String(n).padStart(10, "0");
  return body + String(gs1CheckDigit(body));
}

/** Seed body (UC-TENANCY-014): a positive whole number; the next number becomes one above it. */
export const SeedHighWaterSchema = z.strictObject({ legacyHighWater: z.number().int().min(1) });
export type SeedHighWater = z.infer<typeof SeedHighWaterSchema>;

export const SequenceViewSchema = z.object({
  docType: DocumentSequenceTypeSchema,
  prefix: z.string(),
  separator: z.enum(SEQUENCE_SEPARATORS),
  includeBranchCode: z.boolean(),
  padWidth: z.number().int(),
  /** The counter the next allocation takes. */
  nextSequence: z.number().int(),
  /** The next number as it will print. */
  nextNumber: z.string(),
  lastAllocatedSequence: z.number().int().nullable(),
  lastAllocatedAt: InstantSchema.nullable(),
  legacyHighWater: z.number().int().nullable(),
  /** Numbers issued by Vision X in this sequence (excludes any legacy range). */
  issuedCount: z.number().int(),
  frozen: z.boolean(),
  /** False once a number has been issued: the format and the seed are then immutable (BR-TENANCY-020). */
  editable: z.boolean(),
});
export type SequenceView = z.infer<typeof SequenceViewSchema>;

export const SequenceUpdatedSchema = SequenceViewSchema.extend({ warnings: z.array(z.object({ path: z.string(), code: z.string(), message: z.string() })) });
export const SequencesViewSchema = z.object({ branchId: UlidSchema, branchCode: z.string(), items: z.array(SequenceViewSchema) });
export type SequencesView = z.infer<typeof SequencesViewSchema>;

/** What an allocation answers (§2.7). */
export const AllocatedNumberSchema = z.object({ number: z.string(), sequence: z.number().int() });
export type AllocatedNumber = z.infer<typeof AllocatedNumberSchema>;

// --- Edit leases (UC-TENANCY-017, 018) -----------------------------------------------------------------------------------

/** Lease TTL when the organisation has no edit-lock idle setting (BR-TENANCY-024). */
export const EDIT_LOCK_IDLE_MINUTES_DEFAULT = 5;

/** A record type in a lease key: the document's table name (`quote`, `sales_order`, `purchase_order`, …). */
export const RecordTypeSchema = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/, "a lower-case record type such as quote or sales_order");

/**
 * The permission that allows editing a record type, checked before a lease is granted (BR-TENANCY-027). A type absent here
 * is authenticated-only until its module registers it; the document module's own routes remain the authority on edits.
 */
export const LEASE_EDIT_PERMISSIONS: Readonly<Record<string, PermissionCode>> = {
  sales_order: "sales.order.write",
  purchase_order: "purchasing.po.write",
  prescription: "clinical.prescription.write",
  stock_take: "inventory.stock_take.write",
  patient: "patients.patient.write",
  appointment: "appointments.appointment.write",
  product: "catalogue.product.write",
};

export const LeaseHolderSchema = z.object({ userId: UlidSchema, displayName: z.string(), controlId: z.number().int() });
export type LeaseHolder = z.infer<typeof LeaseHolderSchema>;

export const LeaseViewSchema = z.object({
  recordType: z.string(),
  recordId: z.string(),
  branchId: UlidSchema,
  holder: LeaseHolderSchema,
  acquiredAt: InstantSchema,
  expiresAt: InstantSchema,
});
export type LeaseView = z.infer<typeof LeaseViewSchema>;

export const TakeOverViewSchema = LeaseViewSchema.extend({
  previousHolder: LeaseHolderSchema.nullable(),
  takenOverAt: InstantSchema,
});
export type TakeOverView = z.infer<typeof TakeOverViewSchema>;
